import { api } from "./api";
import { BoNotificationPage, BoNotificationPageResponse } from "../../models/boNotification";
import { CsvDownloadResponse, isCsvDownloadResponse, readCsvResponse } from "../../utils/functions/readCsvResponse";

const notificationTag = (userId: number) => ({ type: "boNotifications" as const, id: userId });

const emptyNotificationPage = (page: number): BoNotificationPage => ({
  notifications: [],
  unreadCount: 0,
  total: 0,
  page,
  perPage: 20,
});

export const boNotificationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBoNotifications: build.query<BoNotificationPage, { userId: number; page: number }>({
      query: ({ page }) => ({ url: "/admin/notifications", params: { page, per_page: 20 } }),
      transformResponse: (response: BoNotificationPageResponse, _meta, { page }): BoNotificationPage => {
        const data = response?.data;
        const rows = Array.isArray(data?.data) ? data.data : [];
        const pagination = data?.pagination;
        if (!data || !pagination) {
          return emptyNotificationPage(page);
        }
        return {
          notifications: rows.map((notification) => ({
            id: notification.id,
            title: notification.title,
            message: notification.message,
            fileId: notification.file_id,
            createdAt: notification.created_at,
            readAt: notification.read_at,
          })),
          unreadCount: data.unread_count ?? 0,
          total: pagination.total,
          page: pagination.page,
          perPage: pagination.per_page,
        };
      },
      providesTags: (_result, _error, { userId }) => [notificationTag(userId)],
    }),
    markBoNotificationRead: build.mutation<void, { notificationId: string; userId: number }>({
      query: ({ notificationId }) => ({ url: `/admin/notifications/${notificationId}/read`, method: "PATCH" }),
      invalidatesTags: (_result, _error, { userId }) => [notificationTag(userId)],
    }),
    downloadBoNotificationFile: build.query<CsvDownloadResponse, { fileId: string; userId: number }>({
      query: ({ fileId }) => ({
        url: `/admin/notifications/files/${fileId}`,
        method: "GET",
        responseHandler: readCsvResponse,
        validateStatus: isCsvDownloadResponse,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetBoNotificationsQuery,
  useMarkBoNotificationReadMutation,
  useLazyDownloadBoNotificationFileQuery,
} = boNotificationsApi;
